import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import socketService from '@/services/socket';
import OnlineUserItem from './OnlineUserItem';
import { getCurrentUnixTimestamp } from '@/utils/dateTime';
import UserProfileModal, { UserProfileData } from '@/components/profile/UserProfileModal';

interface OnlineUser {
  id: string;
  username: string;
  status: 'online' | 'idle' | 'dnd' | 'offline';
  lastSeenAt: number; // Unix timestamp
  avatarUrl?: string;
  bio?: string;
}

interface OnlineUsersProps {
  channelId: string;
}

const ONLINE_USERS_DEBOUNCE = 2000; // Debounce online users fetch to 2 seconds

export default function OnlineUsers({ channelId }: OnlineUsersProps) {
  const [users, setUsers] = useState<OnlineUser[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfileData | null>(null);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchOnlineUsers = useCallback(async () => {
    try {
      const response = await fetch(`/api/channels/${channelId}/online-users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching online users:', error);
    }
  }, [channelId]);

  // Debounced fetch function
  const debouncedFetchOnlineUsers = useCallback(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    fetchTimeoutRef.current = setTimeout(() => {
      fetchOnlineUsers();
    }, ONLINE_USERS_DEBOUNCE);
  }, [fetchOnlineUsers]);

  useEffect(() => {
    // Small delay to ensure socket is ready
    const timer = setTimeout(() => {
      // Fetch initial online users for the channel (not debounced for initial load)
      fetchOnlineUsers();

      // Listen for presence updates
      socketService.on('presence:update', handlePresenceUpdate);
      socketService.on('presence:user-online', handleUserOnline);
      socketService.on('presence:user-offline', handleUserOffline);
      socketService.on('user:online', handleUserOnline);
      socketService.on('user:offline', handleUserOffline);
      socketService.on('user:status-changed', handleStatusChanged);
      socketService.on('user:profile-updated', handleProfileUpdated);

      // Request online users list
      socketService.emit('presence:request', { channelId });
    }, 100);

    return () => {
      clearTimeout(timer);
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      socketService.off('presence:update', handlePresenceUpdate);
      socketService.off('presence:user-online', handleUserOnline);
      socketService.off('presence:user-offline', handleUserOffline);
      socketService.off('user:online', handleUserOnline);
      socketService.off('user:offline', handleUserOffline);
      socketService.off('user:status-changed', handleStatusChanged);
      socketService.off('user:profile-updated', handleProfileUpdated);
    };
  }, [channelId, fetchOnlineUsers]);

  const handleProfileUpdated = (data: any) => {
    if (data?.userId) {
      setUsers((prev) =>
        prev.map((u) =>
          String(u.id) === String(data.userId) ? { ...u, bio: data.bio } : u,
        ),
      );
      setSelectedUser((prev) =>
        prev && String(prev.id) === String(data.userId) ? { ...prev, bio: data.bio } : prev,
      );
    }
  };

  const handlePresenceUpdate = (data: any) => {
    if (!data.channelId || data.channelId === channelId) {
      setUsers(data.users || []);
    }
  };

  const handleUserOnline = (data: any) => {
    if (!data.channelId || data.channelId === channelId) {
      setUsers((prev) => {
        const existing = prev.find((u) => String(u.id) === String(data.userId));
        if (existing) {
          return prev.map((u) =>
            String(u.id) === String(data.userId)
              ? { ...u, status: data.status || 'online', lastSeenAt: getCurrentUnixTimestamp() }
              : u,
          );
        } else {
          return [
            ...prev,
            {
              id: data.userId,
              username: data.username,
              status: data.status || 'online',
              lastSeenAt: getCurrentUnixTimestamp(),
            },
          ];
        }
      });
    }
  };

  const handleUserOffline = (data: any) => {
    if (!data.channelId || data.channelId === channelId) {
      setUsers((prev) =>
        prev.map((u) =>
          String(u.id) === String(data.userId)
            ? { ...u, status: 'offline', lastSeenAt: data.lastSeenAt || getCurrentUnixTimestamp() }
            : u,
        ),
      );
    }
  };

  const handleStatusChanged = (data: any) => {
    // Update user status in the list
    setUsers((prev) => {
      const existing = prev.find((u) => String(u.id) === String(data.userId));
      if (existing) {
        return prev.map((u) =>
          String(u.id) === String(data.userId)
            ? { ...u, status: data.status, lastSeenAt: getCurrentUnixTimestamp() }
            : u,
        );
      }
      // If user not in list and they're coming online, debounce fetch to avoid rate limiting
      if (data.status === 'online') {
        debouncedFetchOnlineUsers();
      }
      return prev;
    });
  };

  const activeCount = useMemo(() => users.filter((u) => u.status !== 'offline').length, [users]);

  const sortedUsers = useMemo(() => {
    // Sort by status priority: online > idle > dnd > offline
    const statusPriority: Record<string, number> = {
      online: 0,
      idle: 1,
      dnd: 2,
      offline: 3,
    };

    return [...users].sort((a, b) => {
      const aPriority = statusPriority[a.status] ?? 3;
      const bPriority = statusPriority[b.status] ?? 3;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return b.lastSeenAt - a.lastSeenAt;
    });
  }, [users]);

  return (
    <div className="bg-transparent">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full px-4 py-3.5 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-brand-on-surface-variant dark:text-[#c4b5fd] hover:bg-white/5 transition-colors cursor-pointer select-none"
        aria-expanded={!collapsed}
        aria-controls="online-users-list"
      >
        <span>Members ({activeCount} active)</span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${collapsed ? '' : 'rotate-180'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {!collapsed && (
        <div id="online-users-list" className="max-h-[calc(100vh-180px)] overflow-y-auto px-2 space-y-1">
          {sortedUsers.length === 0 ? (
            <div className="px-4 py-6 text-xs text-brand-outline dark:text-brand-on-surface-variant text-center">
              No members in this channel
            </div>
          ) : (
            sortedUsers.map((user) => (
              <OnlineUserItem
                key={user.id}
                user={user}
                onClick={() => setSelectedUser(user)}
              />
            ))
          )}
        </div>
      )}

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={!!selectedUser}
        user={selectedUser}
        onClose={() => setSelectedUser(null)}
      />
    </div>
  );
}
